import LoggingBase from "../../base/loggingbase";
import { EditorOptions, PisaCKEditor, PisaEditor, NO_POSITION } from "pisa-cke5v2-xdev";
import Utils from "../../utils/Utils";
import HtmHelper from "../../utils/HtmHelper";
import ItmMgr from "../../gui/ItmMgr";
import CkEdt5Listener from "./CkEdt5Listener";
import Validator from "../../utils/Validator";
import KeyHdl from "../../key/KeyHdl";
import EscHandler from "../../key/EscHandler";
import DomEventHelper from "../../utils/DomEventHelper";
import CkEdt5Register from "./CkEdt5Register";
import PSA from "../../psa";

/** the global PisaCKEditor instance */
const PCE = PisaCKEditor.getInstance();

/** bubbling events that should not leave the editor */
const BUBBLING_EVENTS_NAMES = [ 'mousedown',
        // 'mouseup',
        'mouseover', 'wheel',
        'selectstart', 'selectionchange', 'dragstart', 'dragover', 'keydown',
        'keyup', 'keypress', 'contextmenu'
    ];
// mouseup events are necessary in order to trigger the resize of the modal
// window containing the editor, which reacts to mouseup;
// so the propagation of the "mouseup" event should not be stopped

/** the common notification code */
const NOTIFICATION = 'CKEDT5V2_NFY';

/** spellchecking property */
const PISA_SPELLCHECKING = 'pisaSpellchecking';

/** default image alignment property */
const PISA_IMAGE_ALIGNMENT = 'defaultImageAlignment';


/**
 * stops the bubbling of an event
 * @param {Event} evt the event that should not bubble further up in the DOM tree
 */
function stopBubbling( evt ) {
	evt.stopPropagation();
}

/**
 * adds the required "stop bubbling" listeners to make the editor work...
 * @param {HTMLElement} container the container div
 */
function setupCke5ContainerListeners( container ) {
	BUBBLING_EVENTS_NAMES.forEach( eventName => {
		container.addEventListener( eventName, stopBubbling );
	} );
}

/**
 * removes the required "stop bubbling" listeners to make the editor work...
 * @param {HTMLElement} container the container div
 */
function removeCke5ContainerListeners( container ) {
	BUBBLING_EVENTS_NAMES.forEach( eventName => {
		container.removeEventListener( eventName, stopBubbling );
	} );
}

/**
 * [Esc] handler class active while a CKEditor-native modal (e.g. the "edit placeholder value" dialog,
 * CRM-1688) is open; registered with the global {@link KeyHdl} so [Esc] closes the dialog instead of
 * being eaten and forwarded to the web server - see {@link CkEdt5V2#onDialogVisibility}.
 */
class CkEdt5EscHandler extends EscHandler {

	/**
	 * constructs a new instance
	 * @param {CkEdt5V2} cke the editor host instance
	 */
	constructor(cke) {
		super();
		this._cke = cke;
	}

	/**
	 * @inheritdoc
	 * @override
	 * @param {KeyboardEvent} evt
	 */
	handleEsc(evt) {
		const cke = this._cke;
		if ( cke && cke.alive && cke.editor ) {
			cke.editor.closeActiveDialog();
			return true;
		}
		return false;
	}
}


export default class CkEdt5V2 extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {Object} properties initialization properties
	 */
	constructor( properties ) {
        super('widgets.ckedt5v2.CkEdt5V2');
        // bind event handlers
        Utils.bindAll(this, [ 'layout', 'onReady', 'onRender' ]);

		const idp = properties.parent;
		this._wdgId = idp;
		this.parent = rap.getObject( idp );
        this.element = document.createElement('div');
        this.parent.append(this.element);
        this.wdgReady = false;
        this.edtReady = false;
        this.txtFont = null;
        this.container = null;
        this._editor = null;
        this._iniCtt = null;
        this._dirty = false;
        this._changeNotified = false;
        this._inSetCtt = false;
        this._hasFocus = false;
        this._secondary = false;
        this._plainText = false;
        this._dbgEnabled = this.isDebugEnabled();
        // editor preferences (spell checking, default image alignment) are persisted per user
        // on the backend and delivered via the custom widget data (CRM-1409). They are read in
        // _createEditor(); default the spell-check flag off until then.
        this._spellChecking = false;
        this._uuid = null;
        // [Esc] handler active while a CKEditor-native dialog is open (CRM-1688)
        this._escHandler = new CkEdt5EscHandler(this);
        this._dialogOpen = false;
        // create the editor instance
		const cwd = this.parent.getData('pisasales.CSTPRP.CWD') || {};
        this._createEditor(cwd);
        // remaining initalization
        const parent = this.parent;
        const self = this;
        // add "render" listener (RAP event)
        rap.on('render', this.onRender );
        // add resize listener
        parent.addListener('Resize', this.layout);
        // add focus listeners
        parent.addListener('FocusIn', () => self._onRapFocus(true));
        parent.addListener('FocusOut', () => self._onRapFocus(false));
    }   

    /**
     * @returns {String} the widget ID
     */
    get wdgId() {
        return this._wdgId;
    }

    /**
     * @returns {String} the UUID of this editor instance
     */
    get uuid() {
        return this._uuid;
    }

    /**
     * @returns {Boolean} true if this instance is fully set up and running; false otherwise
     */
    get ready() {
        return this.wdgReady && this.edtReady;
    }

    /**
     * @returns {PisaEditor | null} the editor
     */
    get editor() {
        return this._editor;
    }

    /**
     * returns the current "dirty" state;
     * @returns {Boolean} the current "dirty" state
     */
    get dirty() {
        return this._dirty;
    }

    /**
     * sets the "dirty" state;
     * @param {Boolean} d new value
     */
    set dirty(d) {
        this._dirty = !!d;
    }

    /**
     * @returns {Boolean} true if the editor has the focus, false otherwise
     */
    get hasFocus() {
        return this._hasFocus;
    }

    /**
     * @returns {Boolean} true if this is a secondary editor instance; false otherwise
     */
    get secondary() {
        return this._secondary;
    }

    /**
     * @returns {Boolean} true if the editor is set to "plain text only"; false otherwise
     */
    get plainText() {
        return this._plainText;
    }

    /**
     * @inheritdoc
     * @override
     */
    destroy() {
        super.destroy();
    }

    /**
     * @inheritdoc
     * @override
     */
    doDestroy() {
        // guarded debug-logging call sites check this cached flag rather than isDebugEnabled()
        // (measurably more expensive, called from many hot paths) - clear it here so none of them
        // keep firing (and triggering checkAlive()'s "Instance not longer alive!" warning) on a
        // late call after disposal
        this._dbgEnabled = false;
        CkEdt5Register.getInstance().rmvEditor(this);
        this.wdgReady = this.edtReady = false;
        delete this._iniCtt;
        if ( this._dialogOpen ) {
            KeyHdl.getInstance().rmvEscHandler(this._escHandler);
            this._dialogOpen = false;
        }
        delete this._escHandler;
        const editor = this.editor;
        if ( editor ) {
            this._editor = null;
            editor.destroy();
        }
        const container = this.container;
        if ( container instanceof HTMLElement ) {
            this.container = null;
            removeCke5ContainerListeners(container);
            HtmHelper.rmvDomElm(container);
        }
        this.element = null;
        super.doDestroy();
    }

    layout() {
        if ( this.element && this.wdgReady ) {
            const area = this.parent.getClientArea();
            const elm = this.element;
            elm.style.left = '' + area[0] + 'px';
            elm.style.top = '' + area[1] + 'px';
            elm.style.width = '' + area[2] + 'px';
            elm.style.height = '' + Math.max(0, area[3]-1) + 'px';
        }
    }

    onReady() {
        const elm = this.element;
        if ( (elm instanceof HTMLElement) && (elm.parentElement instanceof HTMLElement) ) {
            // set additional CSS properties to make sure editor's UI elements
            // that should go outside of DIV container's "limit"/"margin" (such
            // as color dropdowns) aren't "cut" by the DIV container's "limit"/
            // "margin" (the widget DIV has fixed width and absolute position)
            const pe = elm.parentElement;
            const zid = Number(pe.style.zIndex);
            pe.style.zIndex = String(zid + 10);
            pe.classList.add('cke5-container-widget');
            this.wdgReady = true;
            this.layout();
        }
    }

    onRender() {
        rap.off('render', this.onRender);
        this.onReady();
    }

    /**
     * called if editor's content was changed
     * @param {PisaEditor} editor the editor who's content was changed
     */
    onChanged(editor) {
        if ( this.isTraceEnabled() ) {
            this.trace('Got a CHANGED notification. "in set ctt"=', this._inSetCtt);
        }
        if ( this.alive && !this._inSetCtt && !this.dirty && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug('Editor reported CHANGED content.');
            }
            // mark dirty synchronously, in the same call stack "change:data" fires within - any
            // synchronous check of this.dirty before the deferred flush below actually runs (e.g.
            // onFocus()'s blur check) must already see it, or it could wrongly conclude there is
            // nothing to flush yet (the CRM-1771 class of bug, here at its root: this flag used to
            // flip only inside the deferred callback, leaving a window where a racing blur saw a stale
            // "false" and skipped flushing). This also re-arms the guard above against any further
            // "change:data" events until the flush below has run, coalescing a burst of them into a
            // single notification - taking over the job a dedicated "in change handling" flag used to.
            this.dirty = true;
            const self = this;
            // defer the actual notification (full content serialization + RAP round-trip) by one
            // macrotask so a burst of keystrokes in a large document doesn't pay that cost on every
            // single one of them; harmless to defer since dirty is already correctly set above for any
            // synchronous consumer.
            window.setTimeout( () => {
                if ( !self.alive ) {
                    // the widget was destroyed while this flush was pending (e.g. the dialog hosting
                    // it was closed within the same 1ms window) - do exactly nothing; checkAlive()
                    // would throw via any of the methods below, and there is nothing left to notify a
                    // backend counterpart that no longer exists.
                    return;
                }
                if ( !self.dirty ) {
                    // something else already resolved the dirty transition in the meantime (e.g. a
                    // racing blur flushed content via onFocus(), or endEditMode()/monitorChanges()
                    // reset it) - there is nothing left to report; in particular, this must NOT fall
                    // through to the "edtDirty" fallback below, which exists for the "change
                    // monitoring is off" case (_notifyChanged() always short-circuits there) and would
                    // otherwise misreport an already-flushed change as still-pending dirty state.
                    if ( self._dbgEnabled ) {
                        self.debug('Deferred CHANGED flush skipped: already resolved in the meantime.');
                    }
                    return;
                }
                if ( self._dbgEnabled ) {
                    self.debug('Reporting CHANGED content to the backend now.');
                }
                if ( !self._notifyChanged() ) {
                    // no notification sent, but we became "dirty" --> notify about this!
                    self._notifyBE('edtDirty', { edtDirty: true });
                }
            }, 1);
        }
    }

    /**
     * called on focus changes
     * @param {PisaEditor} editor the editor who's content was modified
     * @param {Boolean} focus true if the editor gained the focus; false if it lost the focus
     */
    onFocus(editor, focus) {
        if ( this.alive && (this.editor === editor) ) {
            this.log("New editor focus:", focus);
            const has_focus = !!focus;
            if ( this.hasFocus !== has_focus ) {
                this._hasFocus = has_focus;
                const par = { focus: this.hasFocus, send: false };
                if ( !has_focus && this.dirty ) {
                    this._getCttParam(par);
                    this._dirty = false;
                    this._changeNotified = true;
                }
                this._notifyBE('focus', par);
            }
        } else {
            // must use the raw console here: this branch is the dead-instance case, and LoggingBase's
            // own methods call checkAlive() which would throw. Never log the editor instance itself:
            // devtools/console retain logged objects, and the editor graph is so cross-referenced that
            // one retained reference pins it all (CRM-1434). Log the widget id as a hint instead.
            console.warn("Instance is dead, cannot handle focus changes anymore!", focus, this.wdgId);
        }
    }

    /**
     * called if an object link command was selected by the user
     * @param {PisaEditor} editor the editor firing this event
     * @param {String} name the name of the object link command
     */
    onObjectLinkCommand(editor, name) {
        if ( this.alive && (this.editor === editor) ) {
            this._notifyBE('objectLinkCommand', this._getCttParam({ tag: name }));
        }
    }

    /**
     * called if an object link was clicked
     * @param {PisaEditor} editor the editor firing this event
     * @param {String} href the link that was clicked
     */
    onObjectLinkClicked(editor, href) {
        if ( this.alive && (this.editor === editor) ) {
            this._notifyBE('objectLinkClicked', this._getCttParam({ href: href }));
        }
    }

    /**
     * called if the "insert image" command was invoked
     * @param {PisaEditor} editor the editor firing this event
     */
    onInsertImage(editor) {
        if ( this.alive && (this.editor === editor) ) {
            this._notifyBE('insertImage', this._getCttParam(null));
            this._dirty = false;
            this._changeNotified = true;
        }
    }

    /**
     * called if the "toggle text type" event was fired
     * @param {PisaEditor} editor the editor firing this event
     * @param {Boolean} plain new "plain text" state
     */
    onToggleType(editor, plain) {
        const pt = !!plain;
        if ( this.alive && (this.editor === editor) && (this.plainText !== pt) ) {
            // the backend must do the conversion!
            const par = { plain: plain };
            this._notifyBE('toggleType', this._getCttParam(par));
            this._dirty = false;
            this._changeNotified = true;
        }
    }

    /**
     * called if the user has clicked the "maximize" command
     * @param {PisaEditor} editor the editor firing this event
     */
    onMaximize(editor) {
        if ( this.alive && (this.editor === editor) ) {
            this._notifyBE('maximize', this._getCttParam(null));
        }
    }

    /**
     * called if a property change event was fired
     * @param {PisaEditor} editor the editor firing this event
     * @param {String} name name of property
     * @param {*} value new value
     */
    onPropertyChange(editor, name, value) {
        if ( this.alive && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug(`Property changed: "${name}" = "${value}".`);
            }
            switch ( name ) {
                case PISA_SPELLCHECKING:
                    this._spellChecking = !!value;
                    // CRM-1409: persist per user on the backend (was browser local storage)
                    this._notifyBE('propertyChange', { name: name, value: this._spellChecking });
                    break;
                case PISA_IMAGE_ALIGNMENT:
                    // CRM-1409: persist the chosen default image alignment on the backend
                    this._notifyBE('propertyChange', { name: name, value: value });
                    break;
                default:
                    break;
            }
        }
    }

    /**
     * called if the "delete all" command was triggered
     * @param {PisaEditor} editor the editor firing this event
     */
    onDeleteAll(editor) {
        if ( this.alive && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug('Delete all content requested');
            }
            this._notifyBE('deleteAll', this._getCttParam(null));
        }
    }

    /**
     * called if the "AI assist" command was triggered; sends the currently
     * selected text to the backend and awaits an answer via {@link setAiResponse}.
     * The editor has locked itself into a "busy" (read-only) state and stays
     * locked until the answer arrives, so the round-trip *must* be completed.
     *
     * The selected text is passed through *as delivered by the editor*: in an
     * HTML document it therefore carries its surrounding markup (e.g.
     * `<span style="...">...</span>`). We deliberately do NOT strip it here —
     * the application server is able to process HTML and gets the full text
     * including its formatting, so it can decide what to do with it. In plain
     * text mode the editor already hands us plain text.
     * @param {PisaEditor} editor the editor firing this event
     * @param {String} text the currently selected text (HTML in an HTML document)
     * @param {Boolean} plain true if the editor is in "plain text" mode
     */
    onAiAssist(editor, text, plain) {
        if ( this.alive && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug('AI assist requested for selected text.');
            }
            this._notifyBE('aiAssist', this._getCttParam({ text: text || '', plain: !!plain }));
        }
    }

    /**
     * called by the backend to complete a pending "AI assist" request. On
     * success the answer is inserted into the editor; if the request was
     * cancelled (e.g. the application server refused to enter edit mode) the
     * content is left unchanged. Either way the editor's "busy" (read-only)
     * lock set when the request was issued is released.
     * @param {*} args the answer arguments; if {@code args.cancelled} is true
     *        no content is inserted and the editor is only released
     */
    setAiResponse(args) {
        if ( this.ready ) {
            const cancelled = !!args.cancelled;
            if ( cancelled ) {
                if ( this._dbgEnabled ) {
                    this.debug('AI assist request cancelled by backend.');
                }
                // wake the editor without changing anything
                this.editor.setAiResponse('', this.plainText, true, this.txtFont, true);
            } else {
                const html = !!args.html;
                const text = this._processContent(args.ctt || '', !html);
                const processed = !!args.processed;
                if ( this._dbgEnabled ) {
                    this.debug('Received AI assist response from backend.');
                }
                this.editor.setAiResponse(text, !html, processed, this.txtFont, false);
            }
        }
    }

    /**
     * @override
     * @inheritdoc
     * @param {PisaEditor} editor the editor firing this event
     */
    onPlaceholdersDropdown(editor) {
        if ( this.alive && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug('Placeholder insert requested.');
            }
            this._notifyBE('insertPlaceholder', this._getCttParam(null));
        }
    }

    /**
     * @override
     * @inheritdoc
     * @param {PisaEditor} editor the editor firing this event
     * @param {Number} mode new placeholder mode
     */
    onPlaceholdersMode(editor, mode) {
        if ( this.alive && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug('Placeholder mode changed:', mode);
            }
            // TODO - ignore the very next "changed" notification
        }
    }

    /**
     * @override
     * @inheritdoc
     * @param {PisaEditor} editor the editor firing this event
     */
    onPlaceholdersRefresh(editor) {
        if ( this.alive && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug('Placeholders refresh requested.');
            }
            this._notifyBE('refreshPlaceholders', this._getCttParam(null));
        }
    }

    /**
     * called if the editor wants to know whether editing is allowed (CRM-1042, CRM-1771); one example
     * is the user about to edit the value of a placeholder while the editor shows the resolved values
     * (value mode). The editor has locked itself read-only for the round-trip and stays locked until
     * the answer arrives via {@link applyEditingAllowed}, so the round-trip *must* be completed.
     * {@code purpose} is opaque here - it is just echoed back unmodified via applyEditingAllowed().
     * @param {PisaEditor} editor the editor firing this event
     * @param {String} purpose identifies why editing permission is requested
     */
    onRequestEditing(editor, purpose) {
        if ( this.ready && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug('Editing permission requested for purpose:', purpose);
            }
            this._notifyBE('requestEditing', this._getCttParam({ purpose: purpose }));
        }
    }

    /**
     * called if the editor intercepted a "host" hotkey that must not reach the browser (F5, Ctrl/Cmd+R).
     * The editor package (pisaHostKeys plugin) already stopped the DOM keydown event to prevent a browser
     * reload (CRM-1474); here we forward the keystroke to the common key handler so the web server sees the
     * same global key event it would receive if the focus were outside the editor (e.g. F5 = refresh the
     * current form record instead of reloading the whole page).
     * @param {PisaEditor} editor the editor firing this event
     * @param {String} keystroke the intercepted keystroke in the editor package's normalized notation
     *        (e.g. "F5", "Ctrl+R", "Cmd+R", "Ctrl+Shift+R")
     */
    onHotkey(editor, keystroke) {
        if ( this.alive && (this.editor === editor) ) {
            if ( this._dbgEnabled ) {
                this.debug(`Editor intercepted host hotkey "${keystroke}"; forwarding to the key handler.`);
            }
            KeyHdl.getInstance().sndEditorHotkey(keystroke);
        }
    }

    /**
     * called whenever a CKEditor-native modal (Dialog plugin) opens or closes (CRM-1688), e.g. the
     * "edit placeholder value" dialog. The web client's global key handler intercepts [Esc] in the
     * document capture phase before it can reach the dialog's own DOM listeners, so without this hook
     * the dialog could never be closed via [Esc]. Registers/unregisters a local {@link EscHandler} with
     * {@link KeyHdl} while the dialog is open; it closes the dialog and swallows the keystroke instead
     * of letting {@link KeyHdl} eat it and forward it to the web server.
     * @param {PisaEditor} editor the editor firing this event
     * @param {Boolean} open true if a dialog just opened; false if it just closed
     */
    onDialogVisibility(editor, open) {
        if ( this.alive && (this.editor === editor) ) {
            const dialog_open = !!open;
            if ( dialog_open === this._dialogOpen ) {
                return;
            }
            this._dialogOpen = dialog_open;
            if ( dialog_open ) {
                KeyHdl.getInstance().addEscHandler(this._escHandler);
            } else {
                KeyHdl.getInstance().rmvEscHandler(this._escHandler);
            }
        }
    }

    /**
     * called by the backend to answer a pending "request editing" round-trip (CRM-1042, CRM-1771):
     * releases the editor's read-only lock and tells it whether editing is allowed. This is always
     * invoked for a pending request (even when the answer is the same as last time), so the locked
     * editor never waits forever. {@code args.purpose} is just echoed back as received from
     * {@link onRequestEditing} - it is opaque here, only the editor itself interprets it.
     *
     * When permission is granted, {@code this.dirty} is often already {@code true} at this point -
     * the application server's "first modification" handler usually sets it as part of granting
     * permission, before whatever content change the grant enables has actually happened (there may
     * not even be one yet, e.g. a paste replay still pending an async image upload) - but not always:
     * with no "first modification" handler configured on the backend at all, permission is granted
     * without any dirty push, so this code forces the flag itself via {@link _forceNotifyChanged}
     * rather than relying on the backend having done so. {@link onChanged}'s own change-detection
     * guards on {@code !this.dirty}, so with the flag already set it would otherwise silently ignore
     * the *real* content change once it happens (CRM-1771 bug: pasted content never reached the
     * backend until an unrelated later edit incidentally flushed it).
     * <p>
     * {@link _forceNotifyChanged} is called right here, exactly like {@link insertImage} does after
     * its own host-driven mutation, because a held HTML paste is replayed synchronously into the model
     * by {@code this.editor.applyEditingAllowed()} above - by the time this runs the content is
     * already there and must be flushed before this method returns; {@link onChanged}'s own
     * change-detection would only pick it up one macrotask later (it defers via {@code setTimeout}),
     * which is too late if a blur races it (the exact bug this fixes, one step earlier - see
     * {@link onFocus}'s own {@code this.dirty} flush check).
     * @param {*} args the answer arguments; {@code args.editingAllowed} is the permission flag,
     *        {@code args.purpose} the purpose the original request was for
     */
    applyEditingAllowed(args) {
        if ( this.ready ) {
            const allowed = !!(args && args.editingAllowed);
            const purpose = args && args.purpose;
            if ( this._dbgEnabled ) {
                this.debug('Editing allowed:', allowed, 'for purpose:', purpose);
            }
            this.editor.applyEditingAllowed(allowed, purpose);
            if ( allowed ) {
                this._forceNotifyChanged();
            }
        }
    }

    /**
     * called by the backend to set a new text font
     * @param {*} args font properties
     */
    setTextFont(args) {
        this.txtFont = args || null;
        if ( this.container ) {
            ItmMgr.getInst().setFnt(this.container, this.txtFont);
        }
        this._initDefaultFont();
    }

    /**
     * called by the framework to set the current "dirty" state
     * @param {Boolean} dirty new "dirty" state
     */
    setDirty(dirty) {
        if ( this._dbgEnabled ) {
            this.debug("BACKEND: Dirty state is set to:", dirty);
        }
        this.dirty = !!dirty;
    }

    /**
     * called by the backend to set the current "edit mode"; a toolbar-less instance shows a
     * floating toolbox while it is focused and in edit mode (CRM-1430). The web client cannot
     * derive this state on its own - it is supplied by the backend - so this method carries no
     * local logic and simply forwards the flag to the editor instance.
     * @param {*} args argument object providing the "editMode" flag
     */
    setEditMode(args) {
        if ( this.ready ) {
            this.editor.setEditMode(!!args.editMode);
        }
    }

    setCttTypeInfo(args) {
        if ( this.ready ) {
            const info = args.textFormat || {};
            const mode = Validator.isPositiveInteger(info.phMode) ? info.phMode : 0;
            this.editor.setPlaceholderMode(mode)
        }
    }

    /**
     * called by the backend to set new content
     * @param {*} args new content
     */
    newContent(args) {
        const html = !!args.html;
        const ctt = this._processContent(args.ctt || '', !html);
        const processed = !!args.processed;
        const insert = !!args.insert;
        if ( this.isTraceEnabled() ) {
            this.trace('new content: ', 'html:', html, `; ctt: "${ctt}"`);
        }
        if ( this.ready ) {
            this._setContent(ctt, !html, processed, insert);
        } else {
            this._iniCtt = { ctt: ctt, html: html, processed: processed, insert: insert };
        }
    }

    /**
     * inserts content into the current text
     * @param {*} args content to be inserted
     */
    insertContent(args) {
        const html = !!args.html;
        const ctt = this._processContent(args.ctt || '', !html);
        const processed = !!args.processed;
        const keep_lbr = !html && !processed && !!args.keep_lbr;
        const line = Validator.isNumber(args.line) ? args.line : NO_POSITION;
        const start = Validator.isNumber(args.start) ? args.start : NO_POSITION;
        const end = Validator.isNumber(args.end) ? args.end : NO_POSITION;
        if ( this.ready ) {
            try {
                const editor = this.editor;
                const eff_ctt = keep_lbr ? editor.maskLineBreaks(ctt) : ctt;
                editor.insertContent(eff_ctt, !html, processed, line, start, end, this.txtFont);
            } finally {
                // finally, sent back "content inserted" notification
                this._notifyInserted(true);
            }
        } else {
            this._iniCtt = { ctt: ctt, html: html, processed: processed, insert: true };
        }
    }

    /**
     * deletes all content
     */
    deleteAllContent() {
        if ( this.ready ) {
            this.editor.deleteAllContent();
        }
    }

    /**
     * inserts an object link
     * @param {*} args object link properties
     */
    insertObjectLink(args) {
        if ( this.ready ) {
            const href = args.href;
            const title = args.title;
            if ( Validator.isString(href) && Validator.isString(title) ) {
                if ( this.isTraceEnabled() ) {
                    this.trace(`Inserting object link "${title}" --> ${href}.`);
                }
                this.editor.insertLink(href, title);
            } else {
                this.warn('Insert object link: Invalid arguments!', args);
            }
        } else {
            // ?!?!
        }
    }

    /**
     * inserts an image
     * @param {*} args image properties
     */
    insertImage(args) {
        if ( this.ready ) {
            if ( this._dbgEnabled ) {
                this.debug('Inserting image:', args);
            }
            const url = args.url || '';
            const name = args.alt || '';
            const width = Validator.isNumber(args.width) ? args.width : -1;
            const height = Validator.isNumber(args.height) ? args.height : -1;
            const last = !!args.last;
            if ( Validator.isString(url) && Validator.isString(name) && (width > 0) && (height > 0) ) {
                this.editor.insertImage(url, name, width, height, last);
                if ( last ) {
                    this._forceNotifyChanged();
                }
            } else {
                this.warn('Insert image: Invalid arguments!', args);
            }
        }
    }

    /**
     * selects a text range
     * @param {*} args selection arguments
     */
    selectRange(args) {
        if ( this.ready ) {
            try {
                if ( this._dbgEnabled ) {
                    this.debug('New selection:', args);
                }
                const range = args.range || {};
                const focus = !!args.focus;
                const line = Validator.isNumber(range.row) ? range.row : NO_POSITION;
                const start = Validator.isNumber(range.begin) ? range.begin : NO_POSITION;
                const end = Validator.isNumber(range.end) ? range.end : NO_POSITION;
                if ( (line !== NO_POSITION) && (start !== NO_POSITION) && (end !== NO_POSITION) ) {
                    this.editor.setSelectionRange(line, start, end, focus);
                } else {
                    this.warn('Invalid selection arguments:', args);
                }
            } finally {
                this._notifyInserted(false);
            }
        }
    }

    /**
     * called by the backend after the content type was toggled
     * @param {*} args new content
     */
    toggleContentType(args) {
        if ( this.ready ) {
            const toggled = !!args.toggled;
            if ( toggled ) {
                const ctt = args.ctt || '';
                const html = !!args.html;
                const plain = !!args.plain;
                const processed = !!args.processed;
                if ( html !== plain ) {
                    // ok, proceed
                    this._plainText = plain;
                    if ( plain ) {
                        // there's changed content
                        this._setContent(ctt, plain, processed, false);
                    } else {
                        // just change the "plain text" mode
                        this.editor.setPlainText(plain);
                    }
                } else {
                    // what's this?!
                    this.error('Invalid request sent by the backend!', args);
                }
            } else {
                // backend has rejected this - noting to do so far...
            }
        }
    }

    /**
     * inserts or updates placeholders
     * @param {*} args placeholders data
     */
    setPlaceholders(args) {
        if ( this.ready ) {
            const insert = !!args.insert;
            const selected = args.selected || '';
            const entries = args.entries || [];
            if ( insert && Validator.isString(selected) ) {
                // insert placeholder
                const ph = entries.find((item) => selected === item.key) || {};
                const key = ph.key || '';
                const value = ph.value || '';
                const html = !!ph.html;
                const singleline = !html;
                if ( Validator.isString(key) ) {
                    this.editor.insertPlaceholder(key, value, singleline, html, false);
                }
            } else if ( !insert ) {
                // refresh placeholders
                entries.forEach((e) => e.singleline = !e.html);
                this.editor.refreshPlaceholders(entries);
            }
        }
    }

    /**
     * called by the backend if the framework set the focus to this widget
     */
    forceFocus() {
        if ( this.ready ) {
            this._hasFocus = true;
            this.editor.focus();
        }
    }

    /**
     * causes this editor to loose the focus
     */
    blur() {
        if ( this.alive && this.ready ) {
            try {
                this.onFocus(this.editor, false);
            }
            finally {
                this._hasFocus = false;
                this.editor.blur();
            }
        }
    }

    /**
     * called by the backend if edit mode was terminated
     * @param {*} args new content
     */
    endEditMode(args) {
        try {
            this.newContent(args);
        } finally {
            // always reset "dirty" and "notified"
            this._dirty = false;
            this._changeNotified = false;
            // ending edit mode definitively means edit mode is OFF, so force it here (CRM-1430).
            // This is the single choke-point for save/cancel, so the floating toolbox always hides
            // when editing ends - without relying on a separate edit-mode round-trip.
            if ( this.ready ) {
                this.editor.setEditMode(false);
            }
        }
    }

    /**
     * called by the backend to activate or deactivate monitoring of data changes
     * @param {*} args argument object providing parameters
     */
    monitorChanges(args) {
        const monitor = !!args.monitor;
        // this is the opposite of "notified changed"
        this._changeNotified = !monitor;
    }

    /**
     * called by the backend to request the current content
     */
    requestContent() {
        if ( this.ready ) {
            // we return the current content unconditionally!
            this._changeNotified = true;
            this._dirty = false;
            if ( this._dbgEnabled ) {
                this.debug('Sending current content on backend request.');
            }
            this._notifyBE('contentRequested', this._getCttParam( { send: true } ));
        }
    }

    /**
     * creates the editor instance
     * @param {*} cwd custom widget data
     */
    _createEditor(cwd) {
        const container = document.createElement('div');
		container.id = 'cke5cnt_' + this.wdgId;
		container.className = 'cke5container';
        this.element.appendChild(container);
        setupCke5ContainerListeners(container);
        if ( this.txtFont ) {
            ItmMgr.getInst().setFnt(container, this.txtFont);
        }
        this.container = container;
        this._secondary = !!cwd.secondary;
        const tlb = !!cwd.tlb;
        // CRM-1430: a toolbar-less instance shows a floating toolbox, unless the backend suppresses
        // it via the "no toolbox" flag (the "Do not show toolbox" tag style).
        const floatingToolbox = !tlb && !cwd.no_tbx;
        const extended = !!cwd.extended;
        // the backend gives us a comprehensive text format info (CRM-1770): whether HTML is
        // available at all, the default type for a fresh, empty editor (CRM-1353), and the
        // placeholder mode - the editor package derives everything else (plaintext, phMode) from it.
        const textFormat = cwd.textFormat || {};
        const objmenu = cwd.objmenu || [];
        const self = this;
        const props = {};
        const uuid = Validator.ensureString(cwd.uuid);
        this._uuid = uuid;
        const language = Validator.ensureString(cwd.lng);
        props.imageUploadUrl = Validator.ensureString(cwd.imageUploadUrl);
        const rsc = cwd.resources || {};
        if ( rsc.copyFormatCursor ) {
            props.copyFormatCursor = rsc.copyFormatCursor;
        }
        let def_font = {};
        if ( this.txtFont ) {
            def_font = PCE.createFontDescriptor(this.txtFont);
        }
        props.imageBLOBSel = !!cwd.imageBLOBSel;
        // CRM-1297: opt-out of the word-processor Tab-indent behavior (keep CKEditor's
        // default "Tab selects next widget" navigation). Absent/false => Tab indents.
        props.tabWidgetNav = !!cwd.tabWidgetNav;
        // CRM-1409: user preferences persisted on the backend. The spell-check flag is applied to
        // the editor once it is ready; the default image alignment is handed to the editor as a
        // creation property. Later user changes are reported back via onPropertyChange().
        this._spellChecking = !!cwd.pisaSpellchecking;
        if ( Validator.isString(cwd.defaultImageAlignment) ) {
            props.defaultImageAlignment = cwd.defaultImageAlignment;
        }
        props.defaultFont = def_font;
        const options = PCE.createOptions( {
            logger: self,
            language: language,
            toolbar: tlb,
            // CRM-1430: show a floating toolbox for a toolbar-less instance (unless suppressed by
            // the backend, see above). The editor package ignores this when a fixed toolbar is present.
            floatingToolbox: floatingToolbox,
            extended: extended,
            resize: true,
            textFormat: textFormat,
            objmenu: objmenu,
            properties: props
        } );
        const cbf = (editor, err) => {
            if ( editor ) {
                self._onEdtReady(editor, options);
            } else {
                self.error('Failed to create editor instance!', err);
            }
        };
        // create the editor instance
        PCE.createEditor(uuid, container, options, cbf, new CkEdt5Listener(this));
    }

    /**
     * called if the editor was fully initialized
     * @param {PisaEditor} editor the editor instance
     * @param {EditorOptions} options editor options
     */
    _onEdtReady(editor, options) {
        if ( !(editor instanceof PisaEditor) ) {
            this.error('Invalid editor instance!', editor);
            throw new Error("Invalid editor instance!");
        }
        this._editor = editor;
        this.edtReady = true;
        const area = this.parent.getClientArea();
        const height = area[3];
        if ( height > 0 ) {
            // set initial height!
            editor.setEditorHeight(height);
        }
        // setup the dialog host - hosts dialogs shown by the editor
        this._setupDialogHost();
        // setup default font
        this._initDefaultFont();
        // add key listeners
        const ee = editor.element;
        if ( ee instanceof HTMLElement ) {
            const self = this;
            ee.addEventListener('keydown', (e) => self._onEditorCaptureKeyDown(e), true);
            ee.addEventListener('keyup', (e) => self._onEditorKeyUp(e), false);
        }
        if ( this._iniCtt ) {
            const ini_ctt = this._iniCtt;
            this._iniCtt = null;
            // initial content establishes the mode from its own type - the default must not
            // override it
            this._setContent(ini_ctt.ctt, !ini_ctt.html, !!ini_ctt.processed, !!ini_ctt.insert);
        } else if ( options.textFormat.htmlAvailable && !options.textFormat.defaultHtml ) {
            // fresh, empty editor with both formats available but a plain-text default: start in
            // plain-text mode while keeping the toggle usable (CRM-1353). The editor's natural
            // mode is HTML, so the HTML-default case needs no action.
            editor.setPlainText(true);
            // this._plainText must mirror the editor's actual mode - onToggleType() compares the
            // toggle button's target state against it and silently drops the notification to the
            // backend on a false match (CRM-1600); _setContent() is the only other place that
            // keeps it in sync, and it is skipped here (there is no initial content to set).
            this._plainText = true;
        }
        if ( !options.textFormat.htmlAvailable ) {
            // freeze editor to "plain text" mode (HTML is not available at all)
            editor.setPlainTextFix();
        }
        // apply the persisted spell-check preference (CRM-1409); _setContent also does this when
        // content arrives, but a fresh, empty editor needs it applied here too
        editor.setSpellchecking(this._spellChecking, true);
        // register this instance
        CkEdt5Register.getInstance().addEditor(this);
    }

    _initDefaultFont() {
        if ( this.edtReady && this.txtFont ) {
            const df = PCE.createFontDescriptor(this.txtFont);
            this.editor.setDefaultFont(df);
        }
    }

    /**
     * setup dialog host element if required. "ck-body-wrapper" is a page-global element shared by
     * *every* CKEditor5 instance (it hosts dialogs, balloons, dropdowns,...), so its child count grows
     * as soon as a second editor instance is alive (e.g. another rich-text field, or a secondary/
     * maximized editor) and must not be used to detect "already set up" (CRM-1634 - that assumption
     * broke as soon as more than one editor was on the page, leaving the host unprotected and letting
     * keystrokes like [Backspace] bubble up to the web client's global key handler, which "eats" them).
     * Mark the host element itself instead - it is recreated by CKEditor5 whenever the last editor using
     * it goes away, so the marker naturally resets together with it.
     */
    _setupDialogHost() {
        const elms = document.getElementsByClassName('ck-body-wrapper');
        if ( elms && (elms.length > 0) ) {
            const host = elms[0];       // there should be exactly ONE element
            if ( (host instanceof HTMLElement) && !host._pisaDlgHostSetup ) {
                if ( this.isTraceEnabled() ) {
                    this.trace('Setting up dialog host element.')
                }
                host._pisaDlgHostSetup = true;
                host.addEventListener('keydown', stopBubbling);
                host.addEventListener('keyup', stopBubbling);
                host.addEventListener('keypress', stopBubbling);
            }
        }
    }

    /**
     * handles keydown events in capture phase
     * @param {KeyboardEvent} e the keyboard event
     */
    _onEditorCaptureKeyDown(e) {
        let stop = false;
        const ctrl = e.ctrlKey || e.metaKey;
        const alt = e.altKey;
        const shift = e.shiftKey;
        if ( ctrl && !alt && !shift ) {
            switch ( e.key ) {
                case 's':
                case 'S':
                    // prevent the browser from interfering!
                    stop = true;
                    break;
                default:
                    break;
            }
        }
        if ( stop ) {
            DomEventHelper.stopEvent(e);
        }
    }

    /**
     * checks whether a keystroke is a possible hotkey
     * @param {KeyboardEvent} e the keyboard event
     * @param {boolean} ctrl "ctrl pressed" flag
     * @param {boolean} shift "shift pressed" flag
     * @param {boolean} alt "alt pressed" flag
     * @returns {boolean} true if the keyboard event refers to a possible hotkey
     */
    _checkHotKey(e, ctrl, shift, alt) {
        if ( !alt ) {
            const key = e.key.toUpperCase();
            switch ( key ) {
                case 'C':
                case 'X':
                case 'Y':
                case 'Z':
                    if ( !shift ) {
                        // Ctrl+C|X|Y|Z --> handled by the editor
                        return false;
                    }
                    break;
                case 'V':
                    // [Shift]+Ctrl+V --> handled by the editor
                    return false;
                case 'R':
                    // Ctrl/Cmd+R --> already intercepted on keydown by the editor package and forwarded
                    // via onHotkey() (CRM-1474); do not send it again from the keyup path
                    return false;
                case 'E':
                    if ( shift ) {
                        // Ctrl+Shift+E --> edit placeholder value, handled by the editor (CRM-1042)
                        return false;
                    }
                    break;
                default:
                    break;
            }
        }
        return true;
    }

    /**
     * handles keyboard events sent to the editor
     * @param {KeyboardEvent} e the keyboard event
     */
    _onEditorKeyUp(e) {
        if ( this.isTraceEnabled() ) {
            this.trace('KEYUP', e);
        }
        let handled = false;
        let action = null;
        const ctrl = e.ctrlKey || e.metaKey;
        const alt = e.altKey;
        const shift = e.shiftKey;
        if ( ctrl ) {
            const send_key = this._checkHotKey(e, ctrl, shift, alt);
            if ( send_key ) {
                const dsc = KeyHdl.getInstance().getKeyDscFromDomEvent(e);
                const par = { dsc: dsc };
                const was_dirty = this.dirty;
                if ( was_dirty ) {
                    this._getCttParam(par);
                }
                this._notifyBE('hotkey', par);
                if ( was_dirty ) {
                    this._dirty = false;
                    this._changeNotified = true;
                }
                handled = true;
            }
        } else {
            switch ( e.key ) {
                case 'F2':
                    handled = !alt && !shift && !this.secondary;
                    if ( handled ) {
                        const self = this;
                        action = () => {
                            self.onMaximize(self.editor);
                        };
                    }
                    break;
                default:
                    break;
            }
        }
        if ( handled ) {
            DomEventHelper.stopEvent(e);
        }
        if ( action ) {
            action();
        }
    }

    _onRapFocus(focus) {
        if ( this.alive ) {
            this.log("New RAP focus", focus);
            if ( focus && (this.hasFocus !== focus) ) {
                this.forceFocus();
            }
        } else {
            console.warn("Instance is dead, cannot handle focus changes anymore!", focus, this.wdgId);
        }
    }

    /**
     * processes the content sent by the backend
     * @param {String} org original content as sent by the backend
     * @param {Boolean} plain plain text flag
     * @returns {String} the adjusted content, if required; otherwise the original content
     */
    _processContent(org, plain) {
        if ( plain && Validator.isString(org) && org.endsWith('\n') ) {
            return org.substring(0, org.length-1);
        }
        return org;
    }

    /**
     * passes new content to the editor
     * @param {String} ctt new content
     * @param {Boolean} plain plain text flag
     * @param {Boolean} processed flag whether the content was already processed to match the "plain text" setting
     * @param {Boolean} insert flag whether this method was called as consequence of an insert operation
     */
    _setContent(ctt, plain, processed, insert) {
        const isc = this._inSetCtt;
        try {
            this._inSetCtt = true;
            this._plainText = !!plain;
            this.editor.setContent(ctt, this.plainText, !!processed, this.txtFont);
            this.editor.setSpellchecking(this._spellChecking, true);
            if ( insert ) {
                this._notifyInserted(true);
            }
        } finally {
            this._inSetCtt = isc;
            this._dirty = false;
            this._changeNotified = false;
        }
    }

    /**
     * creates / fills an argument object with current content properties
     * @param {*} args current argument object
     * @returns {*} the filled argument object
     */
    _getCttParam(args) {
        if ( this._dbgEnabled ) {
            this.debug('Sending current content along with the notification code to the backend.');
        }
        const param = args || {};
        const editor = this.editor;
        param.ctt = editor.getContent();
        param.html = !editor.plainText;
        return param;
    }

    /**
     * send notifications to the backend
     * @param {String} code notification code
     * @param {*} par notification parameters
     */
    _notifyBE(code, par) {
        if ( this.wdgReady ) {
            const tms = Date.now();
            const param = {};
            param.cod = code;
            param.par = par;
            param.tms = tms;
            rap.getRemoteObject(this).notify(NOTIFICATION, param);
        }
    }

    /**
     * sends changed content to the backend
     * @returns {Boolean} true if a notification was sent; false otherwise
     */
    _notifyChanged() {
        if ( this.dirty && !this._changeNotified ) {
            if ( this._dbgEnabled ) {
                this.debug('Notify backend about CONTENT CHANGED.');
            }
            this._changeNotified = true;
            this._dirty = false;
            this._notifyBE('changed', this._getCttParam(null));
            return true;
        }
        return false;
    }

    /**
     * forces a fresh, synchronous flush of the current content right after a host-driven mutation
     * that {@link onChanged}'s own change-detection would otherwise only pick up one macrotask later
     * (it defers via {@code setTimeout}) - too late if something synchronous races it (e.g. a blur;
     * see {@link applyEditingAllowed} and {@link insertImage}, the two callers of this helper). If
     * nothing actually changed yet, this only resends already-known content as a harmless no-op flush.
     * <p>
     * {@link _notifyChanged} leaves {@code this._changeNotified} {@code true} afterwards as an
     * anti-resend marker for the flush it just performed - reset it back to {@code false} once done
     * here, or it would silently swallow the *next* genuine change's own call to {@link _notifyChanged}
     * too (CRM-1771 class of bug, reproduced one step later instead of fixed).
     */
    _forceNotifyChanged() {
        this._changeNotified = false;
        this._dirty = true;
        this._notifyChanged();
        this._changeNotified = false;
    }

    _notifyInserted(send_ctt) {
        if ( this._dbgEnabled ) {
            this.debug('Notify backend about CONTENT INSERTED.');
        }
        const param = { with_ctt: !!send_ctt };
        if ( send_ctt ) {
            this._changeNotified = true;
            this._dirty = false;
            this._getCttParam(param);
        }
        this._notifyBE('contentInserted', param);
    }

	/** register custom widget type */
	static register() {
		console.debug('Registering custom widget CkEdt5V2.');
		rap.registerTypeHandler( "psawidget.CkEdt5V2", {
			factory: function( properties ) {
				return new CkEdt5V2( properties );
			},
			destructor: 'destroy',
			properties: [ 'textFont', 'dirty', 'cttTypeInfo' ],
			methods: [ 'newContent', 'toggleContentType', 'insertContent', 'deleteAllContent', 'insertObjectLink', 'insertImage', 'selectRange', 'forceFocus', 'endEditMode', 'monitorChanges', 'requestContent', 'setPlaceholders', 'setAiResponse', 'applyEditingAllowed', 'setEditMode' ],
			events: [ NOTIFICATION, 'HTM_EDR_IMG' ]
		} );
	}

}